import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Phone, MapPin, Clock, MessageCircle } from "lucide-react";
import { Reveal, Overline } from "@/components/motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CONTACT } from "@/data/site";

const empty = { name: "", phone: "", email: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast.error("Please add your name, phone and message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm(empty);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Try calling us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-36 bg-foreground text-background" data-testid="contact-section">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <Reveal><Overline className="text-background/50">Get In Touch</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 font-serif text-4xl md:text-6xl lg:text-7xl font-light tracking-tight leading-none">
              Say hello.
            </h2>
          </Reveal>

          <div className="mt-12 space-y-8">
            <a href={`tel:${CONTACT.phoneTel}`} className="flex items-center gap-4 group" data-testid="contact-phone">
              <Phone size={18} className="text-background" />
              <span className="font-serif text-2xl md:text-3xl font-light group-hover:text-background/60 transition-colors">{CONTACT.phone}</span>
            </a>
            <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center gap-4 group" data-testid="contact-whatsapp">
              <MessageCircle size={18} className="text-background" />
              <span className="text-base font-light text-background/80 group-hover:text-background transition-colors">WhatsApp us — {CONTACT.phone}</span>
            </a>
            <div className="flex items-start gap-4">
              <MapPin size={18} className="text-background mt-1 shrink-0" />
              <span className="text-base font-light text-background/80 max-w-sm leading-relaxed">{CONTACT.address}</span>
            </div>
            <div className="flex items-center gap-4">
              <Clock size={18} className="text-background" />
              <span className="text-base font-light text-background/80">{CONTACT.hours}</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <form onSubmit={submit} className="border border-background/20 p-8 md:p-12" data-testid="enquiry-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
                <label className="block">
                  <span className="text-xs uppercase tracking-widest text-background/50">Name</span>
                  <input data-testid="enquiry-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name"
                    className="mt-2 w-full bg-transparent border-0 border-b border-background/30 py-2 outline-none focus:border-background transition-colors text-background placeholder:text-background/40" />
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-widest text-background/50">Phone</span>
                  <input data-testid="enquiry-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Mobile number"
                    className="mt-2 w-full bg-transparent border-0 border-b border-background/30 py-2 outline-none focus:border-background transition-colors text-background placeholder:text-background/40" />
                </label>
                <label className="block md:col-span-2">
                  <span className="text-xs uppercase tracking-widest text-background/50">Email (optional)</span>
                  <input data-testid="enquiry-email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com"
                    className="mt-2 w-full bg-transparent border-0 border-b border-background/30 py-2 outline-none focus:border-background transition-colors text-background placeholder:text-background/40" />
                </label>
                <label className="block md:col-span-2">
                  <span className="text-xs uppercase tracking-widest text-background/50">Message</span>
                  <textarea data-testid="enquiry-message" rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="How can we help?"
                    className="mt-2 w-full bg-transparent border-0 border-b border-background/30 py-2 outline-none focus:border-background transition-colors text-background placeholder:text-background/40 resize-none" />
                </label>
              </div>
              <button type="submit" disabled={loading} data-testid="enquiry-submit"
                className="group relative mt-10 w-full bg-background text-foreground py-4 text-sm font-medium uppercase tracking-widest overflow-hidden disabled:opacity-60">
                <span className="relative z-10 flex items-center justify-center gap-2 transition-colors duration-300 group-hover:text-background">
                  {loading && <Loader2 size={16} className="animate-spin" />}
                  {loading ? "Sending…" : "Send Message"}
                </span>
                <span className="absolute inset-0 bg-foreground translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
